import { jitter, Page } from '@msrllabs/pt-api';
import { API_CONFIG } from '@msrllabs/pt-api/http';

import { appendSseContext, beginRealtimeAttempt, CORRELATION_ID_HEADER, RealtimeAttempt } from '@/observability';

export const emptyPage = <T>(): Page<T> => ({ content: [], totalElements: 0, totalPages: 0 });

export let fetchResponse: (url: string, accessToken: string, opts?: RequestInit) => Promise<Response> = async (
  url,
  accessToken,
  opts
) => {
  if (API_CONFIG.isLogStdout) console.log(new Date(), `${opts?.method ?? 'GET'} ${url}`);
  let response = await fetch(url, {
    ...opts,
    headers: { ...(opts?.headers ?? {}), Authorization: accessToken },
  });

  let contentType = response.headers.get('content-type') ?? 'application/json';
  // Not verified that every service echoes the header back; stays undefined when it does not.
  let correlationId = response.headers.get(CORRELATION_ID_HEADER) ?? undefined;
  if (response.ok) {
    return response;
  } else if (contentType.startsWith('application/json')) {
    let json = await response.json();
    let message = json?.errors?.[0]?.message;
    // eslint-disable-next-line no-throw-literal
    throw {
      message: 'Server returned HTTP ' + response.status + ': ' + JSON.stringify(json),
      field: message,
      json,
      url,
      httpStatus: response.status,
      correlationId,
    };
  } else {
    let text = await response.text();
    // eslint-disable-next-line no-throw-literal
    throw {
      message: 'Server returned HTTP ' + response.status + ': ' + text,
      text,
      url,
      httpStatus: response.status,
      correlationId,
    };
  }
};

export let fetchJson: <T>(url: string, opts?: RequestInit, token?: string | Promise<string>) => Promise<T> = async (
  url,
  opts,
  token = API_CONFIG.accessToken
) => {
  let response = await fetchResponse(url, await token, opts);
  let contentType = response.headers.get('content-type') ?? 'application/json';
  if (contentType.startsWith('application/json')) {
    return response.json();
  } else {
    // eslint-disable-next-line no-throw-literal
    throw { message: 'Server returned non-JSON ' + response.status };
  }
};

export let fetchText: (url: string, opts?: RequestInit) => Promise<string> = async (url, opts) => {
  let response = await fetchResponse(url, await API_CONFIG.accessToken, opts);
  let contentType = response.headers.get('content-type') ?? 'text/plain; charset=utf-8';
  if (contentType.startsWith('text/plain')) {
    return response.text();
  } else {
    // eslint-disable-next-line no-throw-literal
    throw { message: 'Server returned non-JSON ' + response.status };
  }
};

export let fetchMarketDataJson: <T>(url: string, opts?: RequestInit) => Promise<T> = async (url, opts) => {
  let response = await fetchResponse(url, await getMarketDataAccessToken(), opts);
  return await response.json();
};

export type OnMessage = (e: MessageEvent) => void;
export type OnStateChange = (state: EventSource['readyState']) => void;

/** One entry per active connectSSE stream; healthy, ready sockets skip manual retry. */
const immediateRetries = new Set<() => void>();

/**
 * "Try again now": reconnects every active stream that is not open, skipping any pending
 * backoff wait. Open streams waiting for required data may opt in; no request other than the stream itself is
 * retried, so repeated calls are safe.
 */
export const reconnectStreamsNow = () => immediateRetries.forEach(retry => retry());

export function connectSSE(
  url: string,
  onMessage: OnMessage,
  onConnect?: () => void,
  onOpen?: (e: Event) => void,
  onClose?: () => void,
  retryWhileOpen?: () => boolean,
  onHeartbeat?: OnMessage
) {
  let active = true;
  let reconnectWait = 1000;
  const maxReconnectWait = 64 * 1000;
  let eventSource: EventSource | null = null;
  let timerId: NodeJS.Timeout | undefined;
  let heartbeatTimerId: NodeJS.Timeout | undefined;
  let attemptNumber = 0;
  let generation = 0;
  let wasOffline = typeof navigator !== 'undefined' && !navigator.onLine;
  let activeAttempt: RealtimeAttempt | undefined;
  let isOpen = false;

  const close = () => {
    ++generation;
    isOpen = false;
    eventSource?.close();
    eventSource = null;
    activeAttempt?.closed();
    activeAttempt = undefined;
    clearTimeout(timerId);
    timerId = undefined;
    clearTimeout(heartbeatTimerId);
    onClose?.();
    console.log(`UNSUBSCRIBE ${url}`);
  };

  const reconnect = (error?: unknown) => {
    if (!active) return;
    activeAttempt?.error();
    close();
    if (!active) return;
    reconnectWait = Math.min(reconnectWait * 2, maxReconnectWait);
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      wasOffline = true;
      return;
    }
    const waitTime = jitter(reconnectWait);
    timerId = setTimeout(() => {
      timerId = undefined;
      void connect();
    }, waitTime);
    console.error(`reconnect ${url} in ${waitTime} ms`, error);
  };

  const onOffline = () => {
    wasOffline = true;
    close();
  };
  // Skips any pending backoff wait; shared by network return and "Try again now".
  const reconnectImmediately = () => {
    wasOffline = false;
    close();
    reconnectWait = 1000;
    void connect();
  };
  const onOnline = () => {
    if (!active || (!wasOffline && timerId === undefined)) return;
    reconnectImmediately();
  };
  const retryNow = () => {
    if (active && (!isOpen || retryWhileOpen?.())) reconnectImmediately();
  };

  const connect = async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      wasOffline = true;
      return;
    }
    const thisGeneration = ++generation;
    try {
      const token = await API_CONFIG.accessToken;
      if (!active || generation !== thisGeneration) return;
      activeAttempt = beginRealtimeAttempt('sse', url, ++attemptNumber);
      console.log(`SUBSCRIBE ${url}`);
      onConnect?.();
      if (!active || generation !== thisGeneration) return;
      const source = new EventSource(appendSseContext(url, token, activeAttempt.context));
      eventSource = source;
      const isCurrent = () => active && generation === thisGeneration && eventSource === source;
      const armHeartbeatTimeout = () => {
        clearTimeout(heartbeatTimerId);
        heartbeatTimerId = setTimeout(() => {
          if (isCurrent()) reconnect(new Error('SSE heartbeat timed out'));
        }, 25 * 1000);
      };
      source.addEventListener('error', e => {
        if (isCurrent()) reconnect(e);
      });
      source.addEventListener('message', e => {
        if (isCurrent()) onMessage(e as MessageEvent);
      });
      source.addEventListener('heartbeat', e => {
        if (!isCurrent()) return;
        // Detect support per socket: a reconnect may land on a legacy server without heartbeats.
        armHeartbeatTimeout();
        onHeartbeat?.(e as MessageEvent);
      });
      source.addEventListener('open', e => {
        if (!isCurrent()) return;
        reconnectWait = 1000;
        isOpen = true;
        activeAttempt?.open();
        onOpen?.(e);
      });
    } catch (e) {
      if (active && generation === thisGeneration) reconnect(e);
    }
  };
  window.addEventListener('offline', onOffline);
  window.addEventListener('online', onOnline);
  immediateRetries.add(retryNow);
  void connect();

  return () => {
    active = false;
    immediateRetries.delete(retryNow);
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('online', onOnline);
    close();
  };
}

export const connectSSEWithStateChange = (
  url: string,
  onMessage: OnMessage,
  onStateChange: OnStateChange,
  onConnect?: () => void,
  retryWhileOpen?: () => boolean
) => {
  const onOpen = () => {
    console.info('SSE opened: ' + url);
    onStateChange(EventSource.OPEN);
  };

  onStateChange(EventSource.CLOSED);

  return connectSSE(
    url,
    onMessage,
    () => {
      onConnect?.();
      onStateChange(EventSource.CONNECTING);
    },
    onOpen,
    () => onStateChange(EventSource.CLOSED),
    retryWhileOpen
  );
};

const emptyMarketDataAccessToken = { accessToken: '', expTs: 0 };
let marketDataAccessToken: Promise<{ accessToken: string; expTs: number }> = Promise.resolve(emptyMarketDataAccessToken);

let getMarketDataAccessToken = async () => {
  let { accessToken, expTs } = await marketDataAccessToken.catch(() => emptyMarketDataAccessToken);
  if (Date.now() < expTs) {
    return accessToken;
  } else {
    marketDataAccessToken = (async () => {
      try {
        let { accessToken, exp } = await fetchJson<{ accessToken: string; exp: string }>(
          `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/history/api/v1/tokens/marketData`
        );
        return { accessToken, expTs: +new Date(exp) };
      } catch (err) {
        marketDataAccessToken = Promise.resolve(emptyMarketDataAccessToken);
        throw err;
      }
    })();
  }
  return (await marketDataAccessToken).accessToken;
};
